import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { AttachmentModel, CampaignModel, OrderItemModel } from '@models/types'

import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'
import type { CampaignSearchResult, ImportCampaignOptions } from '@modals/ImportCampaignModal/hooks/useImportCampaign'

import type {
  DownloadParams, EmailParams, MergeParams, ShareParams,
} from '@hooks/useCampaign'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/campaign/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/campaign/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/campaign/FETCH_FAILURE'

const FETCH_RECONCILIATION_TOTALS_REQUEST = 'realbase/campaign/FETCH_RECONCILIATION_TOTALS_REQUEST'
const FETCH_RECONCILIATION_TOTALS_SUCCESS = 'realbase/campaign/FETCH_RECONCILIATION_TOTALS_SUCCESS'
const FETCH_RECONCILIATION_TOTALS_FAILURE = 'realbase/campaign/FETCH_RECONCILIATION_TOTALS_FAILURE'

const CREATE_REQUEST = 'realbase/campaign/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/campaign/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/campaign/CREATE_FAILURE'

const SYNC_REQUEST = 'realbase/campaign/SYNC_REQUEST'
const SYNC_SUCCESS = 'realbase/campaign/SYNC_SUCCESS'
const SYNC_FAILURE = 'realbase/campaign/SYNC_FAILURE'

const UPDATE_REQUEST = 'realbase/campaign/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/campaign/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/campaign/UPDATE_FAILURE'

export type CampaignModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  loadingReconciliationTotals: boolean,
  syncing: boolean,
  updating: boolean,
}

// Initial State
const initialState: CampaignModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  loadingReconciliationTotals: false,
  syncing: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function syncRequest() {
  return {
    type: SYNC_REQUEST,
  }
}

export function syncSuccess(): Action {
  return {
    type: SYNC_SUCCESS,
  }
}

export function syncFailure(errors: Errors = []): Action {
  return {
    type: SYNC_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function fetchReconciliationTotalsRequest(): Action {
  return {
    type: FETCH_RECONCILIATION_TOTALS_REQUEST,
  }
}

export function fetchReconciliationTotalsSuccess(): Action {
  return {
    type: FETCH_RECONCILIATION_TOTALS_SUCCESS,
  }
}

export function fetchReconciliationTotalsFailure(errors: Errors = []): Action {
  return {
    type: FETCH_RECONCILIATION_TOTALS_FAILURE,
    errors,
  }
}

// Action Creators
export function loadCampaigns(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().campaigns.loadedForKeys]
    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/campaigns.json', options)
      .then(({ data }: { data: CampaignModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadCampaign(campaignId: number | string, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().campaigns.loadedForKeys]

    if (entityKey) loadedForKeys.push(entityKey)

    // Set Loading
    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api(`/campaigns/${campaignId}.json`, options)
      .then(({ data }: { data: CampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadCampaignReconciliationTotals(campaignId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch) => {
    dispatch(fetchReconciliationTotalsRequest())

    const promise = api(`/campaigns/${campaignId}/reconciliation_totals.json`, options)
      .then(({ data }: { data: CampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchReconciliationTotalsSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchReconciliationTotalsFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadCampaignUsers(campaignId: number | string, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().campaigns.loadedForKeys]

    if (entityKey) loadedForKeys.push(entityKey)

    // Set Loading
    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api(`/campaigns/${campaignId}/users.json`, options)
      .then(({ data }: { data: CampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.USER_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCampaign(campaign: CampaignModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/campaigns.json', options, config)
      .then(({ data }: { data: CampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateCampaign(campaign: CampaignModel, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      campaign,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaigns/${campaign.id}.json`, options, config)
      .then(({ data }: { data: CampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function toggleHidden(campaign: CampaignModel, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaigns/${campaign.id}/toggle_hidden.json`, options, config)
      .then(({ data }: { data: CampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function togglePublished(campaign: CampaignModel, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(syncRequest())

    const promise = api(`/campaigns/${campaign.id}/toggle_published.json`, options, config)
      .then(({ data }: { data: CampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(syncSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(syncFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function checkRequiredFields(campaign: CampaignModel) {
  return (dispatch: AppDispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/campaigns/${campaign.id}/portal_validation.json`)
      .then(({ data }: { data: number }) => {
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

type CampaignSearchOptions = {
  agency_id: number,
  sale_method: string,
  string: string,
}

export function campaignSearch(options: CampaignSearchOptions) {
  return (dispatch: AppDispatch) => {
    dispatch(fetchRequest())

    const promise = api('/campaigns/api_search.json', options)
      .then(({ data }: { data: CampaignSearchResult }) => {
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function importCampaign(externalId: number, options: ImportCampaignOptions) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      external_id: externalId,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/campaigns/api_import.json', options, config)
      .then(({ data }: { data: CampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function portalSync(campaign: CampaignModel) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      campaign,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(syncRequest())

    const promise = api(`/campaigns/${campaign.id}/portal_sync.json`, {}, config)
      .then(({ data }: { data: { queued: object } }) => {
        dispatch(syncSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(syncFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function bulkPortalRemap(fileData: File, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      csv_file: fileData,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api('/campaigns/bulk_portal_remap.json', options, config)
      .then(({ data }: { data: { queued: boolean } }) => {
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function bulkShareArtworks(
  params: {
    artworkIds?: number[],
    campaign: CampaignModel,
    message: string,
    subject: string,
    userIds?: number[],
    url: string,
  },
  options: EntityOptions = {},
) {
  const {
    artworkIds, campaign, message, subject, url, userIds,
  } = params

  const config = {
    method: 'POST',
    data: JSON.stringify({
      artwork_ids: artworkIds || [],
      message,
      subject,
      url,
      user_ids: userIds || [],
    }),
  }

  return () => {
    const promise = api(`/campaigns/${campaign.id}/bulk_share_artwork.json`, options, config)
      .then(({ data }: { data: { queued: boolean } }) => ({ success: true as const, data }))
      .catch((data) => {
        const errors = handleError(data)

        return { success: false as const, errors }
      })

    return promise
  }
}

export function mergeCampaigns(
  campaign: Partial<CampaignModel>,
  mergeParams: MergeParams,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      ...mergeParams,
    }),
  }
  return (dispatch: AppDispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api(`/campaigns/${campaign.id}/merge_with_campaign.json`, options, config)
      .then(({ data }: { data: CampaignModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function artworkBuilderConfig(campaignId: number) {
  return (dispatch: AppDispatch): Promise<ActionResult<object>> => {
    // Set Loading
    dispatch(fetchRequest())

    const promise = api(`/campaigns/${campaignId}/artwork_builder_config.json`)
      .then(({ data }: { data: object }) => {
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function emailDashboardLink(campaign: CampaignModel, emailParams: EmailParams, options = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      ...emailParams,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/campaigns/${campaign.id}/email_dashboard_link.json`, options, config)
      .then(({ data }: { data: CampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function shareMobileLink(campaign: CampaignModel, shareParams: ShareParams, options = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      ...shareParams,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/campaigns/${campaign.id}/share_mobile_link.json`, options, config)
      .then(({ data }: { data: CampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function shareProject(
  campaign: CampaignModel,
  shareParams: { message: string, link: string, user_ids: number[] },
  options = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      ...shareParams,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/campaigns/${campaign.id}/share_project.json`, options, config)
      .then(({ data }: { data: CampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function checkImageExportSizes(campaign: CampaignModel) {
  return () => {
    const promise = api(`/campaigns/${campaign.id}/check_image_export_sizes.json`)
      .then(({ data }: { data: { image_ids: number[], valid: boolean } }) => ({ success: true as const, data }))
      .catch((data) => {
        const errors = handleError(data)
        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadPendingMedia(campaignId: number, requestOptions?: EntityOptions) {
  return (dispatch: AppDispatch) => {
    const promise = api(`/campaigns/${campaignId}/pending_media.json`, requestOptions)
      .then(({ data }: { data: AttachmentModel[] }) => {
        const normalizedJson = normalize(data, Schemas.ATTACHMENT_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        return { success: false as const, errors }
      })

    return promise
  }
}

export function downloadImages(campaign: CampaignModel, downloadParams: DownloadParams, options = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      ...downloadParams,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaigns/${campaign.id}/download_images.json`, options, config)
      .then(({ data }: { data: { queued: boolean } }) => {
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createPublicToken(campaign: CampaignModel, options = {}) {
  const config = {
    method: 'POST',
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/campaigns/${campaign.id}/create_public_token.json`, options, config)
      .then(({ data }: { data: CampaignModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadPendingAdCopyOrderItems(campaignId: number, requestOptions?: EntityOptions) {
  return (dispatch: AppDispatch) => {
    const promise = api(`/campaigns/${campaignId}/pending_ad_copy_order_items.json`, requestOptions)
      .then(({ data }: { data: OrderItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ITEM_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: CampaignModuleState = initialState,
  action: Action = { type: '' },
): CampaignModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case SYNC_REQUEST:
      return {
        ...state,
        syncing: true,
      }
    case SYNC_SUCCESS:
      return {
        ...state,
        syncing: false,
        errors: [],
      }
    case SYNC_FAILURE:
      return {
        ...state,
        syncing: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case FETCH_RECONCILIATION_TOTALS_REQUEST:
      return {
        ...state,
        loadingReconciliationTotals: true,
      }
    case FETCH_RECONCILIATION_TOTALS_SUCCESS:
      return {
        ...state,
        loadingReconciliationTotals: false,
        errors: [],
      }
    case FETCH_RECONCILIATION_TOTALS_FAILURE:
      return {
        ...state,
        loadingReconciliationTotals: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
